// src/core/preprocess.ts
import frag from '../algorithms/shaders/preprocess.frag?raw';
import { FULLSCREEN_VERT, linkProgram, createFullscreenQuad, createFboTex, type FboTex } from './gl';
import type { DitherParams } from './types';

export const PREPROCESS_FRAG = frag;

export const PREPROCESS_UNIFORM_NAMES = [
  'u_src','u_contrast','u_midtones','u_highlights','u_blur','u_threshold','u_invert','u_texel',
] as const;

export type PreprocessUniformName = typeof PREPROCESS_UNIFORM_NAMES[number];

export interface PreprocessProgram {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  uniforms: Record<PreprocessUniformName, WebGLUniformLocation | null>;
}

export function createPreprocessProgram(gl: WebGL2RenderingContext): PreprocessProgram {
  const program = linkProgram(gl, FULLSCREEN_VERT, frag);
  const vao = createFullscreenQuad(gl);
  const uniforms = Object.fromEntries(
    PREPROCESS_UNIFORM_NAMES.map(n => [n, gl.getUniformLocation(program, n)]),
  ) as PreprocessProgram['uniforms'];
  return { program, vao, uniforms };
}

export function runPreprocess(
  gl: WebGL2RenderingContext,
  prog: PreprocessProgram,
  srcTex: WebGLTexture,
  dst: FboTex,
  width: number,
  height: number,
  p: DitherParams,
): void {
  gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
  gl.viewport(0, 0, width, height);
  gl.useProgram(prog.program);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, srcTex);
  gl.uniform1i(prog.uniforms.u_src, 0);
  gl.uniform1f(prog.uniforms.u_contrast, p.contrast / 100);
  gl.uniform1f(prog.uniforms.u_midtones, p.midtones);
  gl.uniform1f(prog.uniforms.u_highlights, p.highlights / 100);
  gl.uniform1f(prog.uniforms.u_blur, p.blur);
  // threshold > 0 only when user explicitly opts in via the "Apply Threshold" toggle.
  // Shader treats u_threshold > 0 as enabled.
  gl.uniform1f(prog.uniforms.u_threshold, p.thresholdEnabled ? p.threshold / 255 : 0);
  gl.uniform1i(prog.uniforms.u_invert, p.invert ? 1 : 0);
  gl.uniform2f(prog.uniforms.u_texel, 1 / width, 1 / height);
  gl.bindVertexArray(prog.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

export { createFboTex };
